import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner } from 'react-bootstrap'
import { FaRegTrashAlt } from 'react-icons/fa'
import { MdRefresh } from 'react-icons/md'
import moment from 'moment'
import * as scheduleActions from '../../actions/schedule'
import { list as facultyList } from '../../actions/faculty'
import { schoolYear } from '../../actions/group'
import { lk } from '../../actions/lk'
import { MONTHS, transformEducationYear, transformFaculties } from '../../utils/const'
import { getCurrentYear } from '../../utils/getItemFromRange'
import usePageWidth from '../shared/usePageWidth'
import CustomSelect from '../CustomSelect'
import { buildColumns, buildDays } from './buildGrid'
import Grid from './Grid'
import LessonModal from './LessonModal'
import styles from './styles.module.css'

// «Все» в CustomSelect — пустая строка, бэк ждёт 0 как «без отбора»
const ALL = ''
const toParam = value => (value === ALL || value == null ? 0 : value)

const Schedule = () => {
  usePageWidth('pageWide')

  const dispatch = useDispatch()

  const faculties = useSelector(state => state.faculty.list)
  const years = useSelector(state => state.group.years)
  const courses = useSelector(state => state.schedule.educationYear)
  const groups = useSelector(state => state.schedule.groupsSchedule)
  const schedule = useSelector(state => state.schedule.schedule)
  const isLoading = useSelector(state => state.schedule.isLoading)
  const isTeacher = useSelector(state => state.auth.isTeacher)
  const lkData = useSelector(state => state.lk.data)

  const [filters, setFilters] = useState(null)
  // у обучающегося значение по умолчанию — его собственная группа, она приходит
  // вместе с перечнем групп, поэтому хранится отдельно от остальных умолчаний
  const [defaultGroup, setDefaultGroup] = useState(ALL)
  const [lesson, setLesson] = useState(null)
  const pendingStudentGroup = useRef(false)

  useEffect(() => {
    dispatch(facultyList())
    dispatch(schoolYear())
    dispatch(scheduleActions.educationYear())
    dispatch(lk())
    return () => dispatch(scheduleActions.setInitialScehedule())
  }, [dispatch])

  // Значения по умолчанию: текущий учебный год и текущий месяц (п. 2.5 ТЗ).
  // Текущий год определяет сервер признаком is_current — по датам начала и
  // окончания он вычислялся неверно и подставлялся прошлый год.
  const defaults = useMemo(() => {
    const ready = faculties.length && years.length && courses.length
      && (isTeacher || Object.keys(lkData || {}).length)
    if (!ready) return null

    const current = years.find(year => year.is_current) || getCurrentYear(years)
    const studentCourse = !isTeacher && courses.find(course => course.name === lkData.yeducationyear)

    return {
      faculty: (!isTeacher && lkData.facultyid) || faculties[0].idfaculty,
      course: studentCourse ? studentCourse.eyid : ALL,
      year: current && current.xp_key,
      month: moment().month() + 1,
      group: ALL,
    }
  }, [faculties, years, courses, isTeacher, lkData])

  useEffect(() => {
    if (!defaults || filters) return
    setFilters(defaults)
    pendingStudentGroup.current = !isTeacher
  }, [defaults, filters, isTeacher])

  const { faculty, course, year, month, group } = filters || {}

  // перечень групп зависит от факультета, курса и учебного года (п. 2.4 ТЗ)
  useEffect(() => {
    if (!filters) return
    dispatch(scheduleActions.getGroupSchedule({
      faculty: toParam(faculty),
      educationYear: toParam(course),
      year,
    }))
    // перезапрос только при смене этих трёх условий отбора
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faculty, course, year])

  useEffect(() => {
    if (!filters) return
    if (pendingStudentGroup.current) {
      if (!groups.length) return
      const own = groups.find(item => (item.name || item.groupname) === lkData.groupname)
      pendingStudentGroup.current = false
      if (own) {
        setDefaultGroup(own.gid)
        setFilters(prev => ({ ...prev, group: own.gid }))
      }
      return
    }
    // ранее выбранная группа перестала подходить — отбор возвращается к «Все»
    if (group !== ALL && !groups.some(item => String(item.gid) === String(group))) {
      setFilters(prev => ({ ...prev, group: ALL }))
    }
    // проверка выполняется на приход нового перечня групп, а не на смену отбора
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups])

  const fetchSchedule = () => {
    if (!filters) return
    dispatch(scheduleActions.schedule(
      toParam(faculty), toParam(course), year, month, toParam(group)
    ))
  }

  // сетка перестраивается при смене любого условия отбора (п. 11 ТЗ)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSchedule() }, [faculty, course, year, month, group])

  const handleChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }))

  // значения по умолчанию, к которым возвращает «Сбросить отбор» (п. 2.6 ТЗ)
  const initial = defaults && { ...defaults, group: defaultGroup }
  const isDirty = !!filters && !!initial
    && Object.keys(initial).some(key => String(filters[key]) !== String(initial[key]))

  const facultyOptions = useMemo(() => transformFaculties(faculties), [faculties])
  const courseOptions = useMemo(() => transformEducationYear(courses), [courses])
  // порядок учебных годов и групп задаёт сервер, клиент их не пересортировывает
  const yearOptions = useMemo(
    () => years.map(year => ({ name: year.name, value: year.xp_key })),
    [years]
  )
  const groupOptions = useMemo(
    () => groups.map(group => ({ name: group.name || group.groupname, value: group.gid })),
    [groups]
  )

  const columns = useMemo(
    () => buildColumns(groups, schedule, group === ALL ? null : group),
    [groups, schedule, group]
  )
  const days = useMemo(() => buildDays(schedule), [schedule])

  const openLesson = (cellLesson, day, row, column) => setLesson({
    ...cellLesson,
    subtitle: `${day.dayNumber}, ${day.weekdayShort} · `
      + `${row.individual ? 'инд. раб.' : row.periodName} (${row.periodTime}) · ${column.name} группа`,
  })

  const renderField = (label, name, options, widthClass, withoutAll) => (
    <div className={widthClass}>
      <CustomSelect
        title={label}
        width="100%"
        options={options}
        value={filters ? filters[name] : ALL}
        setFieldValue={handleChange}
        valuesName={name}
        disabled={!filters}
        withoutAll={withoutAll}
      />
    </div>
  )

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.headMain}>
          <h1 className={styles.title}>Расписание занятий</h1>
          <p className={styles.subtitle}>
            Занятия учебных групп факультета по дням и парам за выбранный месяц
          </p>
        </div>
        <div className={styles.headRight}>
          <button
            type="button"
            className={styles.resetBtn}
            onClick={() => setFilters(initial)}
            disabled={!isDirty}
          >
            Сбросить отбор
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        {renderField('Факультет', 'faculty', facultyOptions, styles.fFaculty)}
        {renderField('Курс (рота)', 'course', courseOptions, styles.fCourse)}
        {renderField('Учебный год', 'year', yearOptions, styles.fYear, true)}
        {renderField('Месяц', 'month', MONTHS, styles.fMonth, true)}
        {renderField('Группа', 'group', groupOptions, styles.fGroup)}
        <button type="button" className={styles.refreshBtn} onClick={fetchSchedule}>
          <MdRefresh size={17} />
          Обновить
        </button>
      </div>

      <div className={styles.listbox}>
        <div className={styles.frame}>
          {columns.length ? (
            <Grid columns={columns} days={days} onOpenLesson={openLesson} />
          ) : (
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>
                По заданным условиям учебные группы не найдены
              </div>
              Проверьте факультет, курс и учебный год.
            </div>
          )}
        </div>

        {(isLoading || !filters) && (
          <div className={styles.load}>
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        )}
      </div>

      <LessonModal lesson={lesson} onClose={() => setLesson(null)} />
    </div>
  )
}

export default Schedule
